/**
 * Designer's ring sketch — solitaire ring, side elevation view
 * Technical line-drawing style with annotation marks.
 * Includes a draw-on stroke animation.
 */
export function RingSketch({
  className = "",
  animated = true,
}: {
  className?: string;
  animated?: boolean;
}) {
  const stroke = "oklch(0.085 0.004 50)";
  const gold = "oklch(0.48 0.072 52)";

  // Animation lengths (rough estimate of total path length)
  const drawDuration = animated ? "2.4s" : "0s";
  const drawEasing = "cubic-bezier(0.4, 0, 0.2, 1)";

  return (
    <svg
      viewBox="0 0 360 480"
      className={className}
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      <defs>
        <style>{`
          .ring-path {
            stroke-dasharray: 2000;
            stroke-dashoffset: 2000;
            animation: ring-draw ${drawDuration} ${drawEasing} forwards;
          }
          .ring-path-delayed {
            stroke-dasharray: 600;
            stroke-dashoffset: 600;
            animation: ring-draw ${drawDuration} ${drawEasing} 0.4s forwards;
          }
          .ring-path-late {
            stroke-dasharray: 400;
            stroke-dashoffset: 400;
            animation: ring-draw 1.2s ${drawEasing} 1.6s forwards;
          }
          @keyframes ring-draw {
            to { stroke-dashoffset: 0; }
          }
          @media (prefers-reduced-motion: reduce) {
            .ring-path, .ring-path-delayed, .ring-path-late {
              stroke-dashoffset: 0 !important;
              animation: none !important;
            }
          }
        `}</style>
      </defs>

      {/* ── Ring band — outer profile ── */}
      {/* Left outer edge: from bottom-left up to shoulder curve */}
      <path
        className="ring-path"
        d="M 60 400 C 60 380, 62 340, 68 310 C 72 290, 80 278, 95 270 L 155 255"
        fill="none" stroke={stroke} strokeWidth="1.2"
      />
      {/* Right outer edge */}
      <path
        className="ring-path"
        d="M 300 400 C 300 380, 298 340, 292 310 C 288 290, 280 278, 265 270 L 205 255"
        fill="none" stroke={stroke} strokeWidth="1.2"
      />
      {/* Bottom arc of ring */}
      <path
        className="ring-path"
        d="M 60 400 C 60 430, 92 450, 130 455 C 160 459, 200 459, 230 455 C 268 450, 300 430, 300 400"
        fill="none" stroke={stroke} strokeWidth="1.2"
      />
      {/* Inner band — left */}
      <path
        className="ring-path-delayed"
        d="M 98 400 C 98 382, 100 350, 104 325 C 107 308, 113 296, 122 288 L 152 278"
        fill="none" stroke={stroke} strokeWidth="0.8" strokeOpacity="0.5"
      />
      {/* Inner band — right */}
      <path
        className="ring-path-delayed"
        d="M 262 400 C 262 382, 260 350, 256 325 C 253 308, 247 296, 238 288 L 208 278"
        fill="none" stroke={stroke} strokeWidth="0.8" strokeOpacity="0.5"
      />
      {/* Inner bottom arc */}
      <path
        className="ring-path-delayed"
        d="M 98 400 C 98 420, 120 436, 155 440 C 180 443, 200 443, 205 443 C 240 443, 262 428, 262 400"
        fill="none" stroke={stroke} strokeWidth="0.8" strokeOpacity="0.5"
      />

      {/* ── Setting base (basket) ── */}
      <path
        className="ring-path-delayed"
        d="M 155 255 L 148 230 L 148 210 M 205 255 L 212 230 L 212 210"
        fill="none" stroke={stroke} strokeWidth="1"
      />
      {/* Setting horizontal rails */}
      <path
        className="ring-path-delayed"
        d="M 148 230 L 212 230 M 148 220 L 212 220"
        fill="none" stroke={stroke} strokeWidth="0.7" strokeOpacity="0.6"
      />

      {/* ── Prongs (4) ── */}
      <path
        className="ring-path-late"
        d="M 148 210 C 146 196, 148 188, 154 182"
        fill="none" stroke={stroke} strokeWidth="1.5"
      />
      <path
        className="ring-path-late"
        d="M 212 210 C 214 196, 212 188, 206 182"
        fill="none" stroke={stroke} strokeWidth="1.5"
      />
      <path
        className="ring-path-late"
        d="M 168 210 C 166 200, 165 188, 166 178"
        fill="none" stroke={stroke} strokeWidth="1.5"
      />
      <path
        className="ring-path-late"
        d="M 192 210 C 194 200, 195 188, 194 178"
        fill="none" stroke={stroke} strokeWidth="1.5"
      />

      {/* ── Gemstone — brilliant cut (simplified front view) ── */}
      {/* Outer girdle outline */}
      <ellipse
        className="ring-path-late"
        cx="180" cy="155" rx="38" ry="35"
        fill="none" stroke={gold} strokeWidth="1.2"
      />
      {/* Table facet */}
      <ellipse
        className="ring-path-late"
        cx="180" cy="150" rx="18" ry="14"
        fill="none" stroke={gold} strokeWidth="1"
      />
      {/* Crown facet lines — 4 main */}
      <path
        className="ring-path-late"
        d={[
          "M 162 148 L 148 138", "M 198 148 L 212 138",
          "M 180 136 L 180 122", "M 168 142 L 156 128",
          "M 192 142 L 204 128",
        ].join(" ")}
        fill="none" stroke={gold} strokeWidth="0.7" strokeOpacity="0.7"
      />
      {/* Culet line */}
      <path
        className="ring-path-late"
        d="M 180 170 L 180 180"
        fill="none" stroke={gold} strokeWidth="0.7" strokeOpacity="0.5"
      />

      {/* ── Sparkle marks around stone ── */}
      {[
        [133, 128], [227, 128], [180, 100], [148, 110], [212, 110],
      ].map(([x, y], i) => (
        <g key={i} className="ring-path-late" opacity="0.55">
          <line x1={x - 5} y1={y} x2={x + 5} y2={y} stroke={gold} strokeWidth="0.8" />
          <line x1={x} y1={y - 5} x2={x} y2={y + 5} stroke={gold} strokeWidth="0.8" />
          <line x1={x - 3} y1={y - 3} x2={x + 3} y2={y + 3} stroke={gold} strokeWidth="0.5" />
          <line x1={x + 3} y1={y - 3} x2={x - 3} y2={y + 3} stroke={gold} strokeWidth="0.5" />
        </g>
      ))}

      {/* ── Annotation lines ── */}
      {/* Width dimension — bottom of ring */}
      <line x1="40" y1="420" x2="320" y2="420" stroke={stroke} strokeWidth="0.5" strokeDasharray="4 4" strokeOpacity="0.25" />
      <line x1="40" y1="416" x2="40" y2="424" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.25" />
      <line x1="320" y1="416" x2="320" y2="424" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.25" />

      {/* Stone height annotation */}
      <line x1="232" y1="118" x2="232" y2="182" stroke={stroke} strokeWidth="0.5" strokeDasharray="3 4" strokeOpacity="0.25" />
      <line x1="228" y1="118" x2="236" y2="118" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.25" />
      <line x1="228" y1="182" x2="236" y2="182" stroke={stroke} strokeWidth="0.5" strokeOpacity="0.25" />

      {/* Label: PRONG SETTING */}
      <text x="38" y="268" fontFamily="var(--font-sans)" fontSize="7" fill={stroke} opacity="0.3" letterSpacing="1.5">
        PRONG SETTING
      </text>
      {/* Label: BRILLIANT CUT */}
      <text x="248" y="152" fontFamily="var(--font-sans)" fontSize="7" fill={stroke} opacity="0.3" letterSpacing="1.5">
        BRILLIANT CUT
      </text>
      {/* Label: Ø */}
      <text x="172" y="440" fontFamily="var(--font-sans)" fontSize="7" fill={stroke} opacity="0.25" letterSpacing="1">
        BAND PROFILE
      </text>
    </svg>
  );
}
